import { useRef, useState } from 'react'
import { createFeatures, exportKml } from '../api/endpoints'
import type { Mission } from '../api/types'
import { MapImportError, parseMapFile, type ImportedShape, type MapImport } from '../import/mapImport'
import { featureRequestFor, kmlFileNameFor } from '../import/toFeatures'
import { Modal } from './Modal'

/**
 * Map files in and out of an event: import KML, KMZ or an ATAK package, export the map as KML.
 *
 * The dashboard always shares what it imports -- unlike the phone, there is no "only me" here,
 * because a TOC is somewhere people watch together, not somebody's private notes.
 */
export function MapFilePanel({ mission, onImported }: { mission: Mission; onImported: () => void }) {
  const input = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<MapImport | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  async function choose(file: File) {
    setProblem(null)
    try {
      const imported = parseMapFile(new Uint8Array(await file.arrayBuffer()), file.name)
      if (imported.shapes.length === 0) {
        setProblem(`Nothing in ${file.name} could be placed on a map.`)
      }
      setPending(imported)
    } catch (error) {
      setProblem(error instanceof MapImportError ? error.message : `${file.name} could not be read.`)
    }
  }

  async function download() {
    setExporting(true)
    setProblem(null)
    const result = await exportKml(mission.id)
    setExporting(false)
    if (!result.ok) {
      setProblem(result.error.message)
      return
    }
    const blob = new Blob([result.value], { type: 'application/vnd.google-earth.kml+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${kmlFileNameFor(mission.name)}.kml`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <button className="secondary" onClick={() => input.current?.click()}>
          Import file
        </button>
        <button className="secondary" disabled={exporting} onClick={() => void download()}>
          {exporting ? 'Exporting…' : 'Export KML'}
        </button>
      </div>
      <p className="tertiary" style={{ margin: '6px 0 0' }}>
        KML or KMZ from Google Earth, My Maps, Caltopo or QGIS, and ATAK data packages. The export
        opens in all of them.
      </p>
      {problem && (
        <div className="banner warning" style={{ marginTop: 8 }}>
          {problem}
        </div>
      )}
      <input
        ref={input}
        type="file"
        accept=".kml,.kmz,.zip,.cot,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0]
          // Cleared, so choosing the same file again after a fix still fires a change.
          event.target.value = ''
          if (file) void choose(file)
        }}
      />
      {pending && pending.shapes.length > 0 && (
        <ImportModal
          mission={mission}
          imported={pending}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null)
            onImported()
          }}
        />
      )}
    </section>
  )
}

function ImportModal({
  mission,
  imported,
  onClose,
  onDone,
}: {
  mission: Mission
  imported: MapImport
  onClose: () => void
  onDone: () => void
}) {
  // A side only when the viewer is on one: an organiser running the event without a team has no
  // side, and "my side" would mean nobody's.
  const [audience, setAudience] = useState<'MISSION' | 'FACTION'>('MISSION')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const count = (kind: ImportedShape['kind']) => imported.shapes.filter((s) => s.kind === kind).length
  const summary = [
    ['point', 'marker'],
    ['line', 'line'],
    ['area', 'area'],
    ['circle', 'circle'],
  ]
    .map(([kind, word]) => [count(kind as ImportedShape['kind']), word] as const)
    .filter(([n]) => n > 0)
    .map(([n, word]) => `${n} ${word}${n === 1 ? '' : 's'}`)
    .join(' · ')

  async function share() {
    setBusy(true)
    setProblem(null)
    const batch = `dash-${Date.now()}`
    const result = await createFeatures(
      mission.id,
      imported.shapes.map((shape, index) => featureRequestFor(shape, audience, `${batch}-${index}`)),
    )
    setBusy(false)
    if (result.ok) onDone()
    else setProblem(result.error.message)
  }

  return (
    <Modal title={`Import ${imported.fileName}`} onClose={onClose}>
      <div className="stack">
        <p style={{ margin: 0 }}>
          <strong>{imported.format}</strong> · {summary}
        </p>
        {imported.skipped.length > 0 && (
          <details>
            <summary className="tertiary">{imported.skipped.length} left out</summary>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {imported.skipped.slice(0, 50).map((reason, index) => (
                <li key={index} className="tertiary">
                  {reason}
                </li>
              ))}
            </ul>
          </details>
        )}

        <fieldset style={{ border: 0, padding: 0, margin: 0 }} className="stack">
          <legend style={{ marginBottom: 6 }}>Who sees it</legend>
          <label className="row">
            <input
              type="radio"
              name="audience"
              checked={audience === 'MISSION'}
              onChange={() => setAudience('MISSION')}
            />
            Everyone in the event, both sides
          </label>
          {mission.myFactionKey && (
            <label className="row">
              <input
                type="radio"
                name="audience"
                checked={audience === 'FACTION'}
                onChange={() => setAudience('FACTION')}
              />
              My side only
            </label>
          )}
        </fieldset>

        {problem && <div className="banner danger">{problem}</div>}

        <div className="row">
          <button disabled={busy} onClick={() => void share()}>
            {busy ? 'Importing…' : `Add ${imported.shapes.length} to the map`}
          </button>
          <button className="ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  )
}
